import { Button } from "@dune/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
	getSignedPutUrl,
	registerS3File,
	uploadFileToS3,
} from "../../api/queries/files.queries";
import { gfasQueryKeys } from "../../api/query-keys/gfas.query-keys";
import { UploadFilesModal } from "../common/UploadFilesModal";

interface AddGFADocumentButtonProps {
	gfaId: number;
	onUploadSuccess?: (fileId: string) => void;
}

export function AddGFADocumentButton({
	gfaId,
	onUploadSuccess,
}: AddGFADocumentButtonProps) {
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const queryClient = useQueryClient();

	const uploadSingleFile = async (file: File) => {
		const signedUrl = await getSignedPutUrl({
			bucket: "private",
			contentType: file.type,
		});

		await uploadFileToS3(signedUrl.signedUrl, file);

		const registeredFile = await registerS3File({
			bucket: "private",
			key: signedUrl.key,
			name: file.name,
			mimeType: file.type,
			size: file.size,
		});

		onUploadSuccess?.(registeredFile.id);
	};

	const handleUpload = async (files: File[]) => {
		try {
			for (const file of files) {
				await uploadSingleFile(file);
			}

			toast.success(
				files.length > 1
					? `${files.length} fichiers ajoutés avec succès`
					: "Fichier ajouté avec succès",
			);

			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.filesOnGFA(gfaId.toString()),
			});
			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.detail(gfaId.toString()),
			});
		} catch (error) {
			console.error("Erreur lors du téléchargement:", error);
			toast.error("Erreur lors du téléchargement du fichier");
		}
	};

	return (
		<>
			<Button
				type="button"
				variant="blueChip"
				size="sm"
				onClick={() => setIsUploadModalOpen(true)}
				className="h-7 rounded-full"
			>
				<Plus className="w-3 h-3" />
				Ajouter
			</Button>

			<UploadFilesModal
				open={isUploadModalOpen}
				onOpenChange={setIsUploadModalOpen}
				onUpload={handleUpload}
			/>
		</>
	);
}
